import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { QRCodeSVG, QRCodeCanvas } from 'qrcode.react';
import { jsPDF } from 'jspdf';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Search, Download, QrCode, CheckCircle, 
  FileDown, Settings2
} from 'lucide-react';
import { formatDate } from '../utils/dateFormat';

import { API } from '../lib/apiBase';

export default function DistributionPage() {
  const { organization, refreshOrganization } = useAuth();
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [cardFields, setCardFields] = useState([]);
  const [showQRModal, setShowQRModal] = useState(false);
  const [selectedBeneficiary, setSelectedBeneficiary] = useState(null);

  const availableFields = [
    { key: 'first_name', label: 'First Name' },
    { key: 'surname', label: 'Surname' },
    { key: 'phone_number', label: 'Phone' },
    { key: 'child_first_name', label: 'Child Name' },
    { key: 'child_birthdate', label: 'Birthdate' },
    { key: 'diaper_size', label: 'Diaper Size' },
  ];

  const fetchBeneficiaries = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/beneficiaries`);
      setBeneficiaries(response.data);
    } catch (error) {
      toast.error('Error loading data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBeneficiaries();
  }, [fetchBeneficiaries]);

  useEffect(() => {
    if (organization?.card_fields) {
      setCardFields(organization.card_fields);
    } else {
      setCardFields(['first_name', 'surname', 'phone_number', 'diaper_size']);
    }
  }, [organization]);

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(filteredBeneficiaries.map(b => b.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id, checked) => {
    if (checked) {
      setSelectedIds([...selectedIds, id]);
    } else {
      setSelectedIds(selectedIds.filter(i => i !== id));
    }
  };

  const handleSaveCardFields = async () => {
    try {
      await axios.put(`${API}/organizations/mine`, { card_fields: cardFields });
      await refreshOrganization();
      toast.success('Settings saved!');
      setShowSettingsModal(false);
    } catch (error) {
      toast.error('Error saving settings');
    }
  };

  const getCheckInUrl = (beneficiaryId) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/check-in/${beneficiaryId}`;
  };

  const openQRModal = (beneficiary) => {
    setSelectedBeneficiary(beneficiary);
    setShowQRModal(true);
  };

  // Fields to render on the card. child_birthdate is always included (critical
  // identity info for distribution) regardless of the admin's card_fields config.
  const effectiveCardFields = useMemo(() => {
    const base = cardFields.length ? cardFields : ['first_name', 'surname', 'phone_number', 'child_first_name', 'diaper_size'];
    return base.includes('child_birthdate') ? base : [...base, 'child_birthdate'];
  }, [cardFields]);

  const exportSingleCardPDF = async (beneficiary) => {
    const doc = new jsPDF();
    const checkInUrl = getCheckInUrl(beneficiary.id);
    
    doc.setDrawColor(200);
    doc.rect(20, 20, 170, 100);
    
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(`${beneficiary.first_name} ${beneficiary.surname}`, 30, 40);
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    let y = 55;
    effectiveCardFields.forEach(field => {
      if (field === 'first_name' || field === 'surname') return;
      const label = availableFields.find(f => f.key === field)?.label || field;
      const value = beneficiary[field] || '-';
      if (field === 'child_birthdate') {
        doc.setFont('helvetica', 'bold');
        doc.text(`${label}: ${value}`, 30, y);
        doc.setFont('helvetica', 'normal');
      } else {
        doc.text(`${label}: ${value}`, 30, y);
      }
      y += 10;
    });
    
    const qrCanvas = document.createElement('canvas');
    const QRCode = await import('qrcode');
    await QRCode.toCanvas(qrCanvas, checkInUrl, { width: 80 });
    const qrImage = qrCanvas.toDataURL('image/png');
    doc.addImage(qrImage, 'PNG', 140, 40, 40, 40);
    
    doc.save(`card_${beneficiary.first_name}_${beneficiary.surname}.pdf`);
    toast.success('PDF downloaded!');
  };

  const exportSelectedCardsPDF = async () => {
    if (selectedIds.length === 0) {
      toast.error('Please select at least one card');
      return;
    }

    const doc = new jsPDF();
    const QRCode = await import('qrcode');
    const selectedBeneficiaries = beneficiaries.filter(b => selectedIds.includes(b.id));
    
    for (let i = 0; i < selectedBeneficiaries.length; i++) {
      const beneficiary = selectedBeneficiaries[i];
      const checkInUrl = getCheckInUrl(beneficiary.id);
      
      if (i > 0) doc.addPage();
      
      doc.setDrawColor(200);
      doc.rect(20, 20, 170, 100);
      
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text(`${beneficiary.first_name} ${beneficiary.surname}`, 30, 40);
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      let y = 55;
      effectiveCardFields.forEach(field => {
        if (field === 'first_name' || field === 'surname') return;
        const label = availableFields.find(f => f.key === field)?.label || field;
        const value = beneficiary[field] || '-';
        if (field === 'child_birthdate') {
          doc.setFont('helvetica', 'bold');
          doc.text(`${label}: ${value}`, 30, y);
          doc.setFont('helvetica', 'normal');
        } else {
          doc.text(`${label}: ${value}`, 30, y);
        }
        y += 10;
      });
      
      const qrCanvas = document.createElement('canvas');
      await QRCode.toCanvas(qrCanvas, checkInUrl, { width: 80 });
      const qrImage = qrCanvas.toDataURL('image/png');
      doc.addImage(qrImage, 'PNG', 140, 40, 40, 40);
    }
    
    doc.save('distribution_cards.pdf');
    toast.success(`${selectedIds.length} cards exported!`);
  };

  const filteredBeneficiaries = beneficiaries.filter(b =>
    b.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.surname?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getLastDistribution = (beneficiary) => {
    if (!beneficiary.distributions?.length) return null;
    const sorted = [...beneficiary.distributions].sort(
      (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
    );
    return sorted[0];
  };

  return (
    <div className="space-y-6" data-testid="distribution-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1A1A1A] font-['Manrope']">Distribution</h1>
          <p className="text-[#595959] mt-1">
            Beneficiary cards with QR codes for check-in
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => setShowSettingsModal(true)}
            data-testid="card-settings-button"
          >
            <Settings2 className="w-4 h-4 mr-2" />
            Card Fields
          </Button>
          <Button
            variant="outline"
            onClick={exportSelectedCardsPDF}
            disabled={selectedIds.length === 0}
            data-testid="export-selected-button"
          >
            <FileDown className="w-4 h-4 mr-2" />
            Export Selected ({selectedIds.length})
          </Button>
        </div>
      </div>

      {/* Search and Select All */}
      <div className="flex flex-col md:flex-row gap-4 items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#8C8C8C]" />
          <Input
            placeholder="Search beneficiaries..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-distribution"
          />
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            checked={selectedIds.length === filteredBeneficiaries.length && filteredBeneficiaries.length > 0}
            onCheckedChange={handleSelectAll}
            data-testid="select-all-checkbox"
          />
          <span className="text-sm text-[#595959]">Select all</span>
        </div>
      </div>

      {/* Cards Grid */}
      {loading ? (
        <div className="text-center py-12 text-[#8C8C8C]">Loading...</div>
      ) : filteredBeneficiaries.length === 0 ? (
        <div className="text-center py-12 text-[#8C8C8C]">
          No beneficiaries found
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredBeneficiaries.map((beneficiary) => {
            const lastDist = getLastDistribution(beneficiary);
            const isSelected = selectedIds.includes(beneficiary.id);
            
            return (
              <Card 
                key={beneficiary.id}
                className={`card-distribution ${isSelected ? 'ring-2 ring-[#4A5D4E]' : ''}`}
                data-testid={`distribution-card-${beneficiary.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={(checked) => handleSelectOne(beneficiary.id, checked)}
                        data-testid={`select-${beneficiary.id}`}
                      />
                      <div>
                        <h3 className="font-semibold text-[#1A1A1A] font-['Manrope']">
                          {beneficiary.first_name} {beneficiary.surname}
                        </h3>
                        {lastDist && (
                          <span className="text-xs text-[#2E7D32] flex items-center gap-1 mt-1">
                            <CheckCircle className="w-3 h-3" />
                            Last: {formatDate(lastDist.timestamp, organization?.date_format || 'DD/MM/YYYY')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm mb-4">
                    {effectiveCardFields.map(field => {
                      if (field === 'first_name' || field === 'surname') return null;
                      const label = availableFields.find(f => f.key === field)?.label || field;
                      const isBirthdate = field === 'child_birthdate';
                      return (
                        <div key={field} className="flex justify-between">
                          <span className={isBirthdate ? 'text-[#1A1A1A] font-semibold' : 'text-[#8C8C8C]'}>{label}:</span>
                          <span className={isBirthdate ? 'text-[#1A1A1A] font-semibold' : 'text-[#1A1A1A]'}>{beneficiary[field] || '-'}</span>
                        </div>
                      );
                    })}
                  </div>

                  {/* QR Preview */}
                  <div 
                    className="qr-container cursor-pointer hover:bg-[#F0EFEA] transition-colors rounded-lg"
                    onClick={() => openQRModal(beneficiary)}
                    data-testid={`qr-preview-${beneficiary.id}`}
                  >
                    <QRCodeSVG
                      value={getCheckInUrl(beneficiary.id)}
                      size={80}
                      level="M"
                    />
                  </div>

                  <div className="flex gap-2 mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => openQRModal(beneficiary)}
                      data-testid={`view-qr-${beneficiary.id}`}
                    >
                      <QrCode className="w-4 h-4 mr-1" />
                      QR
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => exportSingleCardPDF(beneficiary)}
                      data-testid={`download-card-${beneficiary.id}`}
                    >
                      <Download className="w-4 h-4 mr-1" />
                      PDF
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* QR Modal */}
      <Dialog open={showQRModal} onOpenChange={setShowQRModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] text-center">
              {selectedBeneficiary?.first_name} {selectedBeneficiary?.surname}
            </DialogTitle>
            <DialogDescription className="text-center">QR Code for distribution check-in</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center py-6">
            {selectedBeneficiary && (
              <>
                <div className="bg-white p-4 rounded-xl shadow-inner">
                  <QRCodeSVG
                    value={getCheckInUrl(selectedBeneficiary.id)}
                    size={200}
                    level="H"
                  />
                </div>
                <p className="text-sm text-[#8C8C8C] mt-4 text-center">
                  Scan to record distribution
                </p>
              </>
            )}
          </div>
          <DialogFooter className="flex-col gap-2">
            <Button
              className="w-full bg-[#4A5D4E] hover:bg-[#3D4D40]"
              onClick={() => selectedBeneficiary && exportSingleCardPDF(selectedBeneficiary)}
            >
              <Download className="w-4 h-4 mr-2" />
              Download PDF
            </Button>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                const url = getCheckInUrl(selectedBeneficiary?.id);
                navigator.clipboard.writeText(url);
                toast.success('Link copied!');
              }}
            >
              Copy Check-in Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Modal */}
      <Dialog open={showSettingsModal} onOpenChange={setShowSettingsModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Distribution Card Fields</DialogTitle>
            <DialogDescription>Select fields to show on cards</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            {availableFields.map(field => (
              <div key={field.key} className="flex items-center gap-3">
                <Checkbox
                  checked={cardFields.includes(field.key)}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setCardFields([...cardFields, field.key]);
                    } else {
                      setCardFields(cardFields.filter(f => f !== field.key));
                    }
                  }}
                  data-testid={`field-checkbox-${field.key}`}
                />
                <span>{field.label}</span>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettingsModal(false)}>Cancel</Button>
            <Button 
              className="bg-[#4A5D4E] hover:bg-[#3D4D40]"
              onClick={handleSaveCardFields}
              data-testid="save-card-fields-button"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
